import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Building2, HandHeart, ArrowLeft, Settings as SettingsIcon, LogOut, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function PortalHub() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [settings, setSettings] = useState({ association_name: "جمعية العمرة" });

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/70 backdrop-blur-xl border-b border-emerald-900/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {settings.logo_base64 ? (
              <img src={settings.logo_base64} alt="logo" className="h-11 w-11 rounded-xl object-cover" />
            ) : (
              <div className="h-11 w-11 rounded-xl bg-emerald-900 flex items-center justify-center">
                <Moon className="h-6 w-6 text-amber-400" />
              </div>
            )}
            <div>
              <div className="font-heading text-lg font-bold text-emerald-950">{settings.association_name}</div>
              <div className="text-xs text-slate-500">مرحباً، {user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user?.role === "admin" && (
              <Button
                data-testid="hub-settings-btn"
                variant="ghost"
                onClick={() => navigate("/settings")}
                className="text-emerald-900 hover:text-emerald-800 hover:bg-emerald-50"
              >
                <SettingsIcon className="h-5 w-5 ml-2" />
                الإعدادات
              </Button>
            )}
            <Button
              data-testid="hub-logout-btn"
              variant="ghost"
              onClick={logout}
              className="text-red-600 hover:bg-red-50"
            >
              <LogOut className="h-5 w-5 ml-2" />
              خروج
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-7xl mx-auto px-6 pt-16 pb-8">
        <div className="max-w-3xl">
          <div className="inline-block px-3 py-1 rounded-full bg-emerald-900/5 text-emerald-900 text-xs font-bold tracking-widest uppercase mb-4">
            البوابة الرئيسية
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black text-emerald-950 leading-tight">
            اختر البوابة التي تريد
            <br />
            <span className="text-amber-600">إدارتها اليوم</span>
          </h1>
          <p className="text-slate-600 mt-4 text-lg">
            بوابتان مستقلتان — كل واحدة بمنطقها المالي والإداري الخاص.
          </p>
        </div>
      </div>

      {/* Two portal cards */}
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-8">
          <Link
            data-testid="portal-commercial"
            to="/commercial"
            className="group relative overflow-hidden bg-emerald-900 text-white rounded-2xl p-10 min-h-[360px] flex flex-col justify-between transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-emerald-900/30"
          >
            <div className="absolute inset-0 islamic-pattern opacity-30" />
            <div className="relative z-10 flex items-start justify-between">
              <div className="h-16 w-16 rounded-2xl bg-white/10 backdrop-blur flex items-center justify-center">
                <Building2 className="h-8 w-8 text-amber-400" />
              </div>
              <ArrowLeft className="h-6 w-6 text-white/60 group-hover:text-amber-400 group-hover:-translate-x-2 transition-all" />
            </div>
            <div className="relative z-10">
              <div className="text-amber-400 text-sm font-bold tracking-widest uppercase mb-3">Commercial</div>
              <h2 className="font-heading text-4xl font-black mb-3">البوابة التجارية</h2>
              <p className="text-emerald-100/85 leading-relaxed">
                إدارة الحملات المدفوعة، المستفيدين، المصروفات، وقائمة الدخل مع رسوم بيانية تفاعلية.
              </p>
            </div>
          </Link>

          <Link
            data-testid="portal-charitable"
            to="/charitable"
            className="group relative overflow-hidden bg-white text-emerald-950 rounded-2xl p-10 min-h-[360px] flex flex-col justify-between border-2 border-amber-500/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-amber-600/20 hover:border-amber-500/40"
          >
            <div className="absolute inset-0 gold-pattern opacity-100" />
            <div className="relative z-10 flex items-start justify-between">
              <div className="h-16 w-16 rounded-2xl bg-amber-500/10 flex items-center justify-center">
                <HandHeart className="h-8 w-8 text-amber-600" />
              </div>
              <ArrowLeft className="h-6 w-6 text-emerald-900/40 group-hover:text-amber-600 group-hover:-translate-x-2 transition-all" />
            </div>
            <div className="relative z-10">
              <div className="text-amber-700 text-sm font-bold tracking-widest uppercase mb-3">Charitable</div>
              <h2 className="font-heading text-4xl font-black mb-3">البوابة الخيرية</h2>
              <p className="text-slate-600 leading-relaxed">
                تسجيل مستفيدي العمرة الخيرية مع منع التكرار الذكي وتقارير المستفيدين الفريدين.
              </p>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}
